import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import { InlineLoader } from '../../components/common/Loader';
import { getAdminSettings, updateSettings, uploadAdminLogo, changeAdminPass, setAdminRecoveryPhone } from '../../services/adminService';

function Section({ title, icon, children }) {
  return (
    <div className="card-p space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <i className={`fa-solid ${icon} text-primary text-sm`} />
        <h3 className="text-sm font-bold text-text-primary font-heading">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="input-label">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-text-muted mt-1">{hint}</p>}
    </div>
  );
}

// Secret fields that should NOT be sent if empty (would overwrite existing values)
const SECRET_FIELDS = [
  'razorpayKeySecret', 'turnCredential', 'cashfreeSecretKey',
  'twilioAccountSid', 'twilioAuthToken', 'twilioPhoneNumber',
  'otpApiKey', 'fast2smsApiKey',
];

export default function AdminSettings() {
  const [settings, setSettings]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [logoFile, setLogoFile]   = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [passForm, setPassForm]   = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [isRecoverySet,  setIsRecoverySet]    = useState(false);
  const [passLoading, setPassLoading] = useState(false);

  const [form, setForm] = useState({
    appName: '', convenienceCharge: '', gstPercent: '',
    razorpayKeyId: '', razorpayKeySecret: '',
    cashfreeAppId: '', cashfreeSecretKey: '', cashfreeEnvironment: 'SANDBOX', cashfreeEnabled: false,
    otpProvider: 'twilio', otpEnabled: true,
    twilioAccountSid: '', twilioAuthToken: '', twilioPhoneNumber: '',
    otpApiKey: '', msg91SenderId: '', msg91TemplateId: '',
    fast2smsApiKey: '', fast2smsRoute: 'otp',
    googleMapsApiKey: '',
    turnServerUrl: '', turnUsername: '', turnCredential: '',
    whatsappSupportNumber: '', callSupportNumber: '', contactEmail: '', faqUrl: '', tutorialsUrl: '',
    patientTutorialUrl: '', doctorTutorialUrl: '',
    facebookUrl: '', instagramUrl: '', youtubeUrl: '', whatsappChannelUrl: '',
    dailyPay: '',
  });

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    getAdminSettings()
      .then(res => {
        const s = res.settings || {};
        setSettings(s);
        setIsRecoverySet(s.isRecoverySet || false);
        setForm({
          appName:               s.appName || '',
          convenienceCharge:     s.convenienceCharge ?? '',
          gstPercent:            s.gstPercent ?? '',
          razorpayKeyId:         s.razorpayKeyId || '',
          razorpayKeySecret:     '',   // never pre-fill secrets
          cashfreeAppId:         s.cashfreeAppId        || '',
          cashfreeSecretKey:     '',   // never pre-fill secrets
          cashfreeEnvironment:   s.cashfreeEnvironment  || 'SANDBOX',
          cashfreeEnabled:       s.cashfreeEnabled      || false,
          otpProvider:           s.otpProvider || 'twilio',
          otpEnabled:            s.otpEnabled !== false,
          // Never pre-fill secret keys — user must re-enter to update
          twilioAccountSid: '', twilioAuthToken: '', twilioPhoneNumber: '',
          otpApiKey: '', msg91SenderId: s.msg91SenderId || '', msg91TemplateId: s.msg91TemplateId || '',
          fast2smsApiKey: '', fast2smsRoute: s.fast2smsRoute || 'otp',
          // Store configured flags for UI hints
          _twilioConfigured: s.twilioConfigured || false,
          _msg91Configured:  s.msg91Configured  || false,
          _fast2smsConfigured: s.fast2smsConfigured || false,
          googleMapsApiKey:      s.googleMapsApiKey || '',
          turnServerUrl:         s.turnServerUrl || '',
          turnUsername:          s.turnUsername || '',
          turnCredential:        '',   // never pre-fill
          whatsappSupportNumber: s.whatsappSupportNumber || '',
          callSupportNumber:     s.callSupportNumber || '',
          contactEmail:          s.contactEmail || '',
          faqUrl:                s.faqUrl || '',
          tutorialsUrl:          s.tutorialsUrl || '',
          patientTutorialUrl:    s.patientTutorialUrl || '',
          doctorTutorialUrl:     s.doctorTutorialUrl || '',
          facebookUrl:           s.facebookUrl || '',
          instagramUrl:          s.instagramUrl || '',
          youtubeUrl:            s.youtubeUrl || '',
          whatsappChannelUrl:    s.whatsappChannelUrl || '',
          dailyPay:              s.dailyPay ?? '',
        });
        setLogoPreview(s.appLogo || null);
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogoChange = (file) => {
    if (!file) return;
    // Revoke previous blob URL to prevent memory leak
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleCancelLogo = () => {
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview(settings?.appLogo || null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (logoFile) {
        const fd = new FormData();
        // Send all non-empty values; skip empty secret fields
        Object.entries(form).forEach(([k, v]) => {
          if (v === '' && SECRET_FIELDS.includes(k)) return; // skip empty secrets
          if (v !== '') fd.append(k, v);
        });
        fd.append('logo', logoFile);
        await uploadAdminLogo(fd);
        setLogoFile(null);
      } else {
        // Build JSON payload — strip empty secret fields so backend keeps existing values
        const payload = { ...form };
        SECRET_FIELDS.forEach(k => { if (payload[k] === '') delete payload[k]; });
        // Strip internal UI-only flags
        delete payload._twilioConfigured;
        delete payload._msg91Configured;
        delete payload._fast2smsConfigured;
        await updateSettings(payload);
      }
      toast.success('Settings saved!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetRecovery = async () => {
    const clean = recoveryPhone.replace(/\D/g,'').slice(-10);
    if (clean.length !== 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    setRecoveryLoading(true);
    try {
      await setAdminRecoveryPhone(clean);
      setIsRecoverySet(true);
      setRecoveryPhone('');
      toast.success('Recovery phone set! This number will be used for password reset OTPs.');
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setRecoveryLoading(false); }
  };

  const handleChangePass = async () => {
    const { currentPassword, newPassword, confirmPassword } = passForm;
    if (!currentPassword || !newPassword) { toast.error('All fields required'); return; }
    if (newPassword.length < 6)  { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setPassLoading(true);
    try {
      await changeAdminPass({ currentPassword, newPassword });
      toast.success('Password changed!');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPassLoading(false);
    }
  };

  if (loading) return <AdminLayout title="Settings"><InlineLoader text="Loading settings..." /></AdminLayout>;

  return (
    <AdminLayout title="App Settings">
      <div className="max-w-2xl space-y-5">

        {/* ── Branding ───────────────────────────────────── */}
        <Section title="Branding" icon="fa-palette">
          <Field label="App Logo" hint="Used in PWA install icon and app header">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-surface flex-shrink-0">
                {logoPreview
                  ? <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
                  : <i className="fa-solid fa-image text-text-muted text-xl" />}
              </div>
              <label className="px-4 py-2 border border-border rounded-btn text-sm font-medium text-primary cursor-pointer hover:bg-primary-light transition-colors">
                <i className="fa-solid fa-upload mr-2 text-xs" />Upload Logo
                <input type="file" accept="image/*" className="hidden"
                  onChange={e => { if (e.target.files[0]) handleLogoChange(e.target.files[0]); }} />
              </label>
              {logoFile && (
                <button onClick={handleCancelLogo} className="text-xs text-text-muted">
                  Cancel
                </button>
              )}
            </div>
          </Field>
          <Field label="App Name">
            <input value={form.appName} onChange={e => setF('appName', e.target.value)}
              placeholder="FARISTAA" className="input" />
          </Field>
        </Section>

        {/* ── Payment Settings ───────────────────────────── */}
        <Section title="Payment Settings" icon="fa-indian-rupee-sign">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Convenience Charge (₹)" hint="Charged per booking">
              <input type="number" min={0} value={form.convenienceCharge}
                onChange={e => setF('convenienceCharge', e.target.value)}
                placeholder="e.g. 19" className="input" />
            </Field>
            <Field label="GST (%)" hint="Applied on convenience charge only">
              <input type="number" min={0} max={100} value={form.gstPercent}
                onChange={e => setF('gstPercent', e.target.value)}
                placeholder="e.g. 18" className="input" />
            </Field>
          </div>
        {/* Razorpay removed — Cashfree is now the payment gateway */}
        </Section>

        {/* ── Cashfree Payment Gateway ────────────────────── */}
        <Section title="Cashfree Payment Gateway" icon="fa-credit-card">
          <div className="flex items-center justify-between p-3 bg-surface rounded-xl border border-border mb-3">
            <div>
              <p className="text-sm font-semibold text-text-primary">Enable Cashfree</p>
              <p className="text-xs text-text-muted">Activate Cashfree as payment provider</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={!!form.cashfreeEnabled}
                onChange={e => setF('cashfreeEnabled', e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:bg-primary transition-all after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            </label>
          </div>
          <Field label="Environment">
            <select value={form.cashfreeEnvironment}
              onChange={e => setF('cashfreeEnvironment', e.target.value)}
              className="input">
              <option value="SANDBOX">Sandbox (Test)</option>
              <option value="PRODUCTION">Production (Live)</option>
            </select>
          </Field>
          <Field label="Cashfree App ID">
            <input value={form.cashfreeAppId}
              onChange={e => setF('cashfreeAppId', e.target.value)}
              placeholder="Your Cashfree App ID" className="input" />
          </Field>
          <Field label="Cashfree Secret Key" hint="Leave blank to keep existing secret">
            <input type="password" value={form.cashfreeSecretKey}
              onChange={e => setF('cashfreeSecretKey', e.target.value)}
              placeholder="Leave blank to keep current" className="input" />
          </Field>
          <p className="text-xs text-text-muted mt-1">
            Webhook URL: <code className="bg-surface px-1 rounded text-primary">/api/payments/cashfree/webhook</code>
          </p>
        </Section>

        {/* ── OTP Settings ───────────────────────────────── */}
        <Section title="OTP / SMS Settings" icon="fa-mobile-screen-button">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-3 bg-surface rounded-xl border border-border">
            <div>
              <p className="text-sm font-semibold text-text-primary">OTP Verification</p>
              <p className="text-xs text-text-muted">Disable only in development</p>
            </div>
            <button type="button" onClick={() => setF('otpEnabled', !form.otpEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.otpEnabled ? 'bg-primary' : 'bg-slate-300'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.otpEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Provider selector */}
          <Field label="Active OTP Provider">
            <select value={form.otpProvider} onChange={e => setF('otpProvider', e.target.value)} className="input">
              <option value="twilio">Twilio (Recommended)</option>
              <option value="msg91">MSG91</option>
              <option value="fast2sms">Fast2SMS</option>
            </select>
          </Field>

          {/* ── Twilio fields ── */}
          {form.otpProvider === 'twilio' && (
            <>
              <div className={`text-xs px-3 py-2 rounded-xl border mb-1 ${form._twilioConfigured ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                <i className={`fa-solid ${form._twilioConfigured ? 'fa-circle-check' : 'fa-triangle-exclamation'} mr-1.5`} />
                {form._twilioConfigured ? 'Twilio credentials saved. Leave blank to keep existing.' : 'No Twilio credentials saved yet. Enter below.'}
              </div>
              <Field label="Account SID" hint="From console.twilio.com → Account Info">
                <input type="password" value={form.twilioAccountSid}
                  onChange={e => setF('twilioAccountSid', e.target.value)}
                  placeholder={form._twilioConfigured ? '••••••• (saved)' : 'ACxxxxxxxxxxxxxxxxx'} className="input" />
              </Field>
              <Field label="Auth Token">
                <input type="password" value={form.twilioAuthToken}
                  onChange={e => setF('twilioAuthToken', e.target.value)}
                  placeholder={form._twilioConfigured ? '••••••• (saved)' : 'Your auth token'} className="input" />
              </Field>
              <Field label="Twilio Phone Number" hint="E.164 format e.g. +14155552671">
                <input value={form.twilioPhoneNumber}
                  onChange={e => setF('twilioPhoneNumber', e.target.value)}
                  placeholder={form._twilioConfigured ? '••••••• (saved)' : '+14155552671'} className="input" />
              </Field>
            </>
          )}

          {/* ── MSG91 fields ── */}
          {form.otpProvider === 'msg91' && (
            <>
              <div className={`text-xs px-3 py-2 rounded-xl border mb-1 ${form._msg91Configured ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                <i className={`fa-solid ${form._msg91Configured ? 'fa-circle-check' : 'fa-triangle-exclamation'} mr-1.5`} />
                {form._msg91Configured ? 'MSG91 API Key saved. Leave blank to keep existing.' : 'No MSG91 credentials saved yet.'}
              </div>
              <Field label="MSG91 API Key (Auth Key)">
                <input type="password" value={form.otpApiKey}
                  onChange={e => setF('otpApiKey', e.target.value)}
                  placeholder={form._msg91Configured ? '••••••• (saved)' : 'Your MSG91 auth key'} className="input" />
              </Field>
              <Field label="Sender ID" hint="6-char sender ID e.g. FARIST">
                <input value={form.msg91SenderId}
                  onChange={e => setF('msg91SenderId', e.target.value)}
                  placeholder="FARIST" className="input" maxLength={6} />
              </Field>
              <Field label="Template ID" hint="OTP Template ID from MSG91 dashboard">
                <input value={form.msg91TemplateId}
                  onChange={e => setF('msg91TemplateId', e.target.value)}
                  placeholder="69ba85d35551934cba045212" className="input" />
              </Field>
            </>
          )}

          {/* ── Fast2SMS fields ── */}
          {form.otpProvider === 'fast2sms' && (
            <>
              <div className={`text-xs px-3 py-2 rounded-xl border mb-1 ${form._fast2smsConfigured ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                <i className={`fa-solid ${form._fast2smsConfigured ? 'fa-circle-check' : 'fa-triangle-exclamation'} mr-1.5`} />
                {form._fast2smsConfigured ? 'Fast2SMS API Key saved. Leave blank to keep existing.' : 'No Fast2SMS credentials saved yet.'}
              </div>
              <Field label="Fast2SMS API Key">
                <input type="password" value={form.fast2smsApiKey}
                  onChange={e => setF('fast2smsApiKey', e.target.value)}
                  placeholder={form._fast2smsConfigured ? '••••••• (saved)' : 'Your Fast2SMS API key'} className="input" />
              </Field>
              <Field label="Route" hint="otp (default) or dlt">
                <select value={form.fast2smsRoute} onChange={e => setF('fast2smsRoute', e.target.value)} className="input">
                  <option value="otp">otp (standard)</option>
                  <option value="dlt">dlt (DLT registered)</option>
                </select>
              </Field>
            </>
          )}
        </Section>

        {/* ── API Integrations ───────────────────────────── */}
        <Section title="API Integrations" icon="fa-plug">
          <Field label="Google Maps API Key" hint="For patient location search">
            <input value={form.googleMapsApiKey}
              onChange={e => setF('googleMapsApiKey', e.target.value)}
              placeholder="AIzaSy..." className="input" />
          </Field>
          <Field label="TURN Server URL" hint="For WebRTC video calls behind NAT (optional)">
            <input value={form.turnServerUrl}
              onChange={e => setF('turnServerUrl', e.target.value)}
              placeholder="turn:your-server.com:3478" className="input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="TURN Username">
              <input value={form.turnUsername}
                onChange={e => setF('turnUsername', e.target.value)}
                placeholder="Username" className="input" />
            </Field>
            <Field label="TURN Credential" hint="Leave blank to keep current">
              <input type="password" value={form.turnCredential}
                onChange={e => setF('turnCredential', e.target.value)}
                placeholder="Leave blank to keep current" className="input" />
            </Field>
          </div>
        </Section>

        {/* ── Support Contacts ───────────────────────────── */}
        <Section title="Support Contacts" icon="fa-headset">
          <div className="grid grid-cols-2 gap-3">
            <Field label="WhatsApp Support Number" hint="Patients can WhatsApp on this number">
              <input value={form.whatsappSupportNumber}
                onChange={e => setF('whatsappSupportNumber', e.target.value)}
                placeholder="91XXXXXXXXXX" className="input" />
            </Field>
            <Field label="Call Support Number" hint="Patients can call this number">
              <input value={form.callSupportNumber}
                onChange={e => setF('callSupportNumber', e.target.value)}
                placeholder="XXXXXXXXXX" className="input" />
            </Field>
          </div>
          <Field label="Contact Email" hint="Support email shown to patients">
            <input type="email" value={form.contactEmail}
              onChange={e => setF('contactEmail', e.target.value)}
              placeholder="support@yourdomain.com" className="input" />
          </Field>
          <Field label="FAQ Page URL" hint="Link to your FAQ page (shown in footer)">
            <input type="url" value={form.faqUrl}
              onChange={e => setF('faqUrl', e.target.value)}
              placeholder="https://yoursite.com/faq" className="input" />
          </Field>
          <Field label="Tutorials URL (General)" hint="Legacy — use the per-audience links below instead">
            <input type="url" value={form.tutorialsUrl}
              onChange={e => setF('tutorialsUrl', e.target.value)}
              placeholder="https://youtube.com/yourplaylist" className="input" />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Patient Tutorial URL" hint="Video tutorial link shown to patients in sidebar">
              <input type="url" value={form.patientTutorialUrl}
                onChange={e => setF('patientTutorialUrl', e.target.value)}
                placeholder="https://youtube.com/patient-guide" className="input" />
            </Field>
            <Field label="Doctor Tutorial URL" hint="Video tutorial link shown to doctors in sidebar">
              <input type="url" value={form.doctorTutorialUrl}
                onChange={e => setF('doctorTutorialUrl', e.target.value)}
                placeholder="https://youtube.com/doctor-guide" className="input" />
            </Field>
          </div>
        </Section>

        {/* ── Social Media Links ────────────────────────── */}
        <Section title="Social Media Links" icon="fa-share-nodes">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Facebook URL">
              <input type="url" value={form.facebookUrl}
                onChange={e => setF('facebookUrl', e.target.value)}
                placeholder="https://facebook.com/yourpage" className="input" />
            </Field>
            <Field label="Instagram URL">
              <input type="url" value={form.instagramUrl}
                onChange={e => setF('instagramUrl', e.target.value)}
                placeholder="https://instagram.com/yourprofile" className="input" />
            </Field>
            <Field label="YouTube URL">
              <input type="url" value={form.youtubeUrl}
                onChange={e => setF('youtubeUrl', e.target.value)}
                placeholder="https://youtube.com/@yourchannel" className="input" />
            </Field>
            <Field label="WhatsApp Channel URL">
              <input type="url" value={form.whatsappChannelUrl}
                onChange={e => setF('whatsappChannelUrl', e.target.value)}
                placeholder="https://whatsapp.com/channel/..." className="input" />
            </Field>
          </div>
        </Section>

        {/* ── Daily Pay (Rozey Pay) ──────────────────────── */}
        <Section title="Daily Pay (Rozey Pay)" icon="fa-calendar-day">
          <Field label="Daily Platform Revenue Target (₹)" hint="Admin internal target for daily earnings — not shown to doctors or patients">
            <input type="number" min={0} value={form.dailyPay}
              onChange={e => setF('dailyPay', e.target.value)}
              placeholder="e.g. 5000" className="input" />
          </Field>
        </Section>

        {/* Save */}
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving
            ? <span className="flex items-center justify-center gap-2">
                <i className="fa-solid fa-spinner animate-spin" />Saving...
              </span>
            : <span><i className="fa-solid fa-floppy-disk mr-2" />Save All Settings</span>}
        </button>

        {/* ── Recovery Phone (for forgot password OTP) ──── */}
        {!isRecoverySet ? (
        <Section title="Recovery Phone for Forgot Password" icon="fa-phone-volume">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3">
            <div className="flex items-start gap-2">
              <i className="fa-solid fa-triangle-exclamation text-amber-500 text-sm mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-800">Fixed Recovery Number</p>
                <p className="text-xs text-amber-700 leading-relaxed mt-0.5">
                  This phone number is <strong>permanent</strong> — set once for forgot password OTP.
                  After saving, this section will be hidden for security.
                </p>
              </div>
            </div>
          </div>
          <Field label="Recovery Phone" hint="10-digit Indian mobile number.">
            <input type="tel" value={recoveryPhone}
              onChange={e => setRecoveryPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
              placeholder="e.g. 9876543210" className="input"
              maxLength={10} />
          </Field>
          <button onClick={handleSetRecovery} disabled={recoveryLoading || recoveryPhone.length < 10}
            className="px-5 py-2.5 bg-amber-500 text-white rounded-btn text-sm font-semibold flex items-center gap-2">
            {recoveryLoading ? <i className="fa-solid fa-spinner animate-spin" /> : <i className="fa-solid fa-phone text-sm" />}
            Set Recovery Phone
          </button>
        </Section>
        ) : (
          <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-2xl flex items-center gap-3">
            <i className="fa-solid fa-circle-check text-success text-lg" />
            <div>
              <p className="text-sm font-semibold text-green-800">Recovery phone is set</p>
              <p className="text-xs text-text-muted">For security, this number is hidden. Contact support to change it.</p>
            </div>
          </div>
        )}

        {/* ── Change Password ────────────────────────────── */}
        <Section title="Change Admin Password" icon="fa-key">
          <Field label="Current Password">
            <input type="password" value={passForm.currentPassword}
              onChange={e => setPassForm(p => ({ ...p, currentPassword: e.target.value }))}
              placeholder="Current password" className="input" />
          </Field>
          <Field label="New Password">
            <input type="password" value={passForm.newPassword}
              onChange={e => setPassForm(p => ({ ...p, newPassword: e.target.value }))}
              placeholder="Min 6 characters" className="input" />
          </Field>
          <Field label="Confirm New Password">
            <input type="password" value={passForm.confirmPassword}
              onChange={e => setPassForm(p => ({ ...p, confirmPassword: e.target.value }))}
              placeholder="Repeat new password" className="input" />
          </Field>
          <button onClick={handleChangePass} disabled={passLoading}
            className="px-5 py-2.5 bg-primary text-white rounded-btn text-sm font-semibold flex items-center gap-2">
            {passLoading ? <i className="fa-solid fa-spinner animate-spin" /> : <i className="fa-solid fa-key text-sm" />}
            Change Password
          </button>
        </Section>

      </div>
    </AdminLayout>
  );
}
